import styled from 'styled-components'
import { Copy, Tooltip, formatDisplayNumber, t } from '@stokr/components-library'
import { InfoIcon, SectionSkeleton } from 'components/sto/Section'
import { formatChartMoney } from 'components/ui/chart'
import { useRedeemPanel } from 'hooks/sto/useRedeemPanel'
import { stoColor, stoSpace, stoType } from 'constants/stoTheme'
import { Notice } from './Notice'
import { FieldGroup, FieldLabel, InfoCard, InfoCardText, InfoCardTitle, PrimaryAction } from './KeyActions.styles'
import { useStoTrack } from 'hooks/sto/useStoTracking'

const Holdings = styled.div`
  display: flex;
  gap: ${stoSpace[6]};
  width: 100%;
`

const Holding = styled.div`
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  min-width: 0;
`

const HoldingValue = styled.p`
  ${stoType.metricValue};
  margin: 0;
  color: ${stoColor.textPrimary};
  font-variant-numeric: tabular-nums;
`

const Fees = styled.p`
  ${stoType.bodySmTight};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${stoSpace[2]};
  margin: 0;
  width: 100%;
  color: ${stoColor.textPrimary};
`

/**
 * Redeem tab (Figma 3469:34512 open · 3474:49449 no notes · 3429:95726 signed out ·
 * 3469:35127 not open). Open or not follows investor-dashboard's Distributions tab; the
 * request itself is made there, so Redeem only navigates to it.
 */
export const RedeemPanel = () => {
  const view = useRedeemPanel()
  const trackSto = useStoTrack()

  if (view.status === 'loading') return <SectionSkeleton rows={3} rowHeight={48} />
  if (view.status === 'closed') {
    return <Notice variant="warning" titleKey="stoPage.keyActions.redeem.notAvailableTitle" />
  }

  const { term, holdings, isChecking, canRedeem, onRedeem } = view

  return (
    <>
      {term && (
        <FieldGroup>
          <FieldLabel>
            <Copy id={term.labelKey} />
          </FieldLabel>
          <InfoCard>
            <InfoCardTitle>
              <Copy id={term.titleKey} values={{ value: term.value }} />
            </InfoCardTitle>
            <InfoCardText>
              <Copy id={term.textKey} />
            </InfoCardText>
          </InfoCard>
        </FieldGroup>
      )}

      {isChecking ? (
        <SectionSkeleton rows={2} rowHeight={40} />
      ) : (
        <>
          {holdings && (
            <Holdings>
              <Holding>
                <FieldLabel>
                  <Copy id="stoPage.keyActions.redeem.notes" values={{ symbol: holdings.symbol }} />
                </FieldLabel>
                <HoldingValue>{formatDisplayNumber(holdings.notes)}</HoldingValue>
              </Holding>
              <Holding>
                <FieldLabel>
                  <Copy id="stoPage.keyActions.redeem.totalAmount" />
                </FieldLabel>
                <HoldingValue>
                  {holdings.amount === undefined ? '–' : formatChartMoney(holdings.amount, holdings.currency)}
                </HoldingValue>
              </Holding>
            </Holdings>
          )}

          <PrimaryAction
            type="button"
            disabled={!canRedeem}
            aria-disabled={!canRedeem}
            data-cy="sto-redeem-cta"
            onClick={() => {
              if (!canRedeem) return
              trackSto('redeem_open_clicked', { location: 'key_actions' })
              onRedeem()
            }}>
            <Copy id="stoPage.keyActions.redeem.cta" />
          </PrimaryAction>

          {canRedeem && (
            <Fees>
              <Copy id="stoPage.keyActions.redeem.fees" />
              <Tooltip placement="top" content={t('stoPage.keyActions.redeem.feesInfo')}>
                <InfoIcon role="button" tabIndex={0} aria-label={t('stoPage.keyInformation.moreInfo')} />
              </Tooltip>
            </Fees>
          )}

          {holdings && !canRedeem && <Notice variant="warning" titleKey="stoPage.keyActions.redeem.openTitle" />}
        </>
      )}
    </>
  )
}
